import { ErrorHandler, NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { LoginModule } from './views/login/login.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { JobPositionsTranslateLoader } from './shared/services/job-positions-translate-loader';
import { RegisterComponent } from './views/register/register.component';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSortModule } from '@angular/material/sort';
import { ApiService } from './shared/services/api.service';
import { CombinedAuthInterceptor } from './shared/combined-auth.interceptor';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialogComponent } from './shared/dialogs/confirm-dialog/confirm-dialog.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { environment, firebaseConfig } from 'src/environments/environment';
import { GoogleService, GOOGLE_MAPS_API_KEY, API_CONFIG } from '@sameta/shared';
import * as Sentry from '@sentry/angular';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAuth, AngularFireAuthModule, PERSISTENCE } from '@angular/fire/compat/auth';

/**
 * Initializer function to wait for Firebase auth state to be restored
 * This prevents API calls from being made before Firebase auth is ready
 */
export function initializeFirebaseAuth(fireAuth: AngularFireAuth): () => Promise<any> {
  return () =>
    new Promise((resolve) => {
      // Set a timeout to avoid blocking app startup indefinitely
      const timeoutId = setTimeout(() => {
        resolve(true);
      }, 3000); // Wait max 3 seconds for auth to restore

      // Wait for auth state to be determined
      const subscription = fireAuth.authState.subscribe((user) => {
        clearTimeout(timeoutId);
        subscription.unsubscribe();

        if (user) {
          // Get current token WITHOUT forcing refresh (false parameter)
          // Token refresh happens automatically via error interceptor when backend returns AUTHORIZATION_EXPIRED
          user
            .getIdToken(false)
            .then((token) => {
              if (typeof localStorage !== 'undefined') {
                localStorage.setItem('session', token);
              }
              resolve(true);
            })
            .catch(() => {
              resolve(true);
            });
        } else {
          resolve(true);
        }
      });
    });
}

// tslint:disable-next-line:typedef
export function JobPositionsLoaderFactory(http: HttpClient) {
  return new JobPositionsTranslateLoader(http);
}

Sentry.init({
  dsn: 'https://f22c1efb9ea8685ecaa89949567b132e@o4508918648209408.ingest.de.sentry.io/4508918678093904',
  debug: false,
  tracesSampleRate: 1,
  environment: `APP-${environment.sentryEnv ? environment.sentryEnv : 'dev'}`,
});

@NgModule({
  declarations: [AppComponent, RegisterComponent, ConfirmDialogComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LoginModule,
    RouterModule.forRoot([{ path: '', component: AppComponent }]),
    AngularFireModule.initializeApp(firebaseConfig),
    AngularFireAuthModule,
    BrowserAnimationsModule,
    CdkStepperModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: JobPositionsLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
    MatCardModule,
    MatDividerModule,
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatSidenavModule,
    MatSortModule,
    ReactiveFormsModule,
    MatIconModule,
    MatDialogModule,
    MatCheckboxModule,
    FooterComponent,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFirebaseAuth,
      deps: [AngularFireAuth],
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CombinedAuthInterceptor,
      multi: true,
    },
    {
      provide: 'apiUrl',
      useValue: environment.apiUrl,
    },
    // API Config for shared services
    {
      provide: API_CONFIG,
      useValue: {
        apiUrl: environment.apiUrl,
        openApiUrl: environment.openApiUrl,
      },
    },
    {
      provide: 'GoogleService',
      useClass: GoogleService,
    },
    {
      provide: PERSISTENCE,
      useValue: 'local',
    },
    {
      provide: GOOGLE_MAPS_API_KEY,
      useValue: environment.googleMapsApiKey,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
